type LogLevel = "log" | "error" | "warn" | "debug" | "info";

export interface LogMessage {
  timestamp: Date;
  type: LogLevel;
  message: string;
}

class ConsoleManager {
  private logBuffer: LogMessage[] = [];
  private listeners: Set<(logs: LogMessage[]) => void> = new Set();
  private originalConsole: { [key in LogLevel]: typeof console.log };
  private queuedUpdates: Array<() => void> = [];
  private isUpdating = false;

  constructor() {
    console.log("🔧 ConsoleManager: Initializing...");
    this.originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      debug: console.debug,
      info: console.info,
    };

    this.interceptConsoleMethods();
    console.log("🔧 ConsoleManager: Initialized successfully");
  }

  private interceptConsoleMethods() {
    const methods: LogLevel[] = ["log", "error", "warn", "debug", "info"];

    methods.forEach((method) => {
      console[method] = (...args: unknown[]) => {
        this.addLog(method, args);
        this.originalConsole[method].apply(console, args);
      };
    });
  }

  private addLog(type: LogLevel, args: unknown[]) {
    const message = args
      .map((arg) => {
        if (arg instanceof Error) {
          return `${arg.message}\n${arg.stack}`;
        }
        return typeof arg === "object"
          ? JSON.stringify(arg, null, 2)
          : String(arg);
      })
      .join(" ");

    const logMessage: LogMessage = {
      timestamp: new Date(),
      type,
      message,
    };

    this.logBuffer.push(logMessage);
    this.queueUpdate(() => this.notifyListeners());
  }

  private queueUpdate(update: () => void) {
    this.queuedUpdates.push(update);

    if (!this.isUpdating) {
      this.processQueue();
    }
  }

  private processQueue() {
    if (this.queuedUpdates.length === 0) {
      this.isUpdating = false;
      return;
    }

    this.isUpdating = true;
    const update = this.queuedUpdates.shift();

    if (update) {
      Promise.resolve().then(() => {
        update();
        this.processQueue();
      });
    }
  }

  subscribe(callback: (logs: LogMessage[]) => void) {
    this.originalConsole.debug("ConsoleManager: New listener subscribed");
    this.listeners.add(callback);

    // Immediately send current logs to new subscriber
    if (this.logBuffer.length > 0) {
      this.queueUpdate(() => callback([...this.logBuffer]));
    }

    return () => {
      this.originalConsole.debug("ConsoleManager: Listener unsubscribed");
      this.listeners.delete(callback);
    };
  }

  private notifyListeners() {
    if (this.listeners.size === 0) {
      this.originalConsole.debug("ConsoleManager: No listeners to notify");
      return;
    }

    this.originalConsole.debug(
      `ConsoleManager: Notifying ${this.listeners.size} listeners of ${this.logBuffer.length} logs`,
    );

    this.listeners.forEach((listener) => {
      try {
        listener([...this.logBuffer]); // Send a copy of the buffer
      } catch (error) {
        this.originalConsole.error(
          "ConsoleManager: Error in console listener:",
          error,
        );
      }
    });
  }

  restore() {
    const methods: LogLevel[] = ["log", "error", "warn", "debug", "info"];
    methods.forEach((method) => {
      console[method] = this.originalConsole[method];
    });
    this.logBuffer = [];
    this.listeners.clear();
    this.queuedUpdates = [];
    this.isUpdating = false;
  }
}

// Create and export singleton instance
export const consoleManager = new ConsoleManager();
